import { useState } from 'react';

interface Props {
  facebookUrl: string;
}

export default function MembershipTiers({ facebookUrl }: Props) {
  const [annual, setAnnual] = useState(true);

  return (
    <section id="membership-tiers" className="py-24 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="text-xs font-bold tracking-widest uppercase text-rose-500 mb-3 block">Two Tiers. No Confusion.</span>
          <h2 className="text-4xl font-extrabold text-gray-900 mb-3">Pick Your Level</h2>
          <p className="text-gray-400 text-sm max-w-sm mx-auto leading-relaxed">
            Free gets you in the door. VIP gets you the shortcut.
          </p>
        </div>

        {/* Billing Toggle */}
        <div className="flex items-center justify-center gap-3 mb-14">
          <span className={`text-sm font-semibold ${!annual ? 'text-gray-900' : 'text-gray-400'}`}>Monthly</span>
          <button
            onClick={() => setAnnual(!annual)}
            className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${annual ? 'bg-rose-500' : 'bg-gray-200'}`}
          >
            <span
              className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${annual ? 'translate-x-7' : 'translate-x-1'}`}
            />
          </button>
          <span className={`text-sm font-semibold ${annual ? 'text-gray-900' : 'text-gray-400'}`}>
            Annual
            <span className="ml-1.5 text-xs bg-rose-100 text-rose-600 font-bold px-2 py-0.5 rounded-full">Save $24/year</span>
          </span>
        </div>

        {/* Two Tier Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {/* INSIDER — Free */}
          <div className="relative rounded-2xl border-2 border-gray-200 bg-white p-8 flex flex-col">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 flex items-center justify-center bg-gray-100 rounded-xl shrink-0">
                <i className="ri-group-line text-gray-600 text-xl"></i>
              </div>
              <div>
                <h3 className="text-base font-extrabold text-gray-900">TMM Insider</h3>
                <p className="text-xs text-gray-400">Your free seat at the table</p>
              </div>
            </div>

            <div className="mb-6 pb-6 border-b border-gray-100">
              <span className="text-4xl font-extrabold text-gray-800">Free</span>
              <span className="text-xs text-gray-400 ml-2">Always free</span>
            </div>

            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">What you get:</p>
            <ul className="space-y-3.5 flex-1 mb-3">
              {[
                { icon: 'ri-group-line', text: 'Access to the private community' },
                { icon: 'ri-calendar-event-line', text: 'Event announcements + RSVP access' },
                { icon: 'ri-compass-3-line', text: 'Camp guide, resources + local hubs' },
                { icon: 'ri-megaphone-line', text: 'Stay in the loop on everything local' },
              ].map((p) => (
                <li key={p.text} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center bg-gray-50 rounded-md mt-0.5 shrink-0">
                    <i className={`${p.icon} text-gray-400 text-xs`}></i>
                  </div>
                  <span className="text-sm text-gray-600 leading-relaxed">{p.text}</span>
                </li>
              ))}
            </ul>

            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 mt-4">What you don&apos;t get:</p>
            <ul className="space-y-2.5 mb-8">
              {[
                'Priority or early access',
                'VIP-only events',
                'Partner discounts',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 opacity-50">
                  <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
                    <i className="ri-close-line text-gray-400 text-xs"></i>
                  </div>
                  <span className="text-sm text-gray-400 line-through leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>

            <a
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3.5 bg-gray-900 hover:bg-gray-800 text-white text-sm font-bold rounded-xl transition-colors whitespace-nowrap cursor-pointer text-center block"
            >
              Join Free via Facebook
            </a>
          </div>

          {/* VIP — Paid */}
          <div className="relative rounded-2xl border-2 border-rose-300 bg-gradient-to-b from-rose-50/80 to-white p-8 flex flex-col ring-2 ring-rose-300 ring-offset-2">
            {/* Badge */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2">
              <span className="bg-rose-500 text-white text-xs font-bold px-5 py-1.5 rounded-full whitespace-nowrap">
                Best Value — Push Annual Hard
              </span>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-11 h-11 flex items-center justify-center bg-rose-100 rounded-xl shrink-0">
                <i className="ri-vip-crown-fill text-rose-600 text-xl"></i>
              </div>
              <div>
                <h3 className="text-base font-extrabold text-gray-900">TMM VIP</h3>
                <p className="text-xs text-rose-500 font-semibold">The shortcut to a better mom life</p>
              </div>
            </div>

            <div className="mb-6 pb-6 border-b border-rose-100">
              {annual ? (
                <div>
                  <span className="text-4xl font-extrabold text-rose-600">$120</span>
                  <span className="text-xs text-gray-400 ml-1.5">/year</span>
                  <p className="text-xs text-rose-400 font-semibold mt-1">= $10/month · 2 months free</p>
                </div>
              ) : (
                <div>
                  <span className="text-4xl font-extrabold text-rose-600">$12</span>
                  <span className="text-xs text-gray-400 ml-1.5">/month</span>
                  <p className="text-xs text-gray-400 mt-1">or $120/year — save $24</p>
                </div>
              )}
            </div>

            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">Everything in Insider, plus:</p>
            <ul className="space-y-3.5 flex-1 mb-8">
              {[
                { icon: 'ri-flashlight-line', text: 'Early RSVP access 24–48 hrs before public' },
                { icon: 'ri-lock-unlock-line', text: 'First access to limited-cap events (bounces, brunches, trips)' },
                { icon: 'ri-shield-star-line', text: 'VIP-only events — smaller, curated, better' },
                { icon: 'ri-price-tag-3-line', text: 'No RSVP fees on most events' },
                { icon: 'ri-percent-line', text: 'Exclusive discounts with Mama Approved vendors' },
                { icon: 'ri-file-text-line', text: 'Exclusive seasonal guides (school breaks, trips)' },
                { icon: 'ri-medal-line', text: '"VIP" badge inside the community' },
              ].map((p) => (
                <li key={p.text} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center bg-rose-50 rounded-md mt-0.5 shrink-0">
                    <i className={`${p.icon} text-rose-500 text-xs`}></i>
                  </div>
                  <span className="text-sm text-gray-700 leading-relaxed">{p.text}</span>
                </li>
              ))}
            </ul>

            <a
              href={facebookUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3.5 bg-rose-500 hover:bg-rose-600 text-white text-sm font-bold rounded-xl transition-colors whitespace-nowrap cursor-pointer text-center block mb-3"
            >
              {annual ? 'Get VIP — $120/year' : 'Get VIP — $12/month'}
            </a>
            <p className="text-center text-xs text-gray-400">Cancel anytime · No long-term commitment</p>
          </div>
        </div>

        {/* Bottom note */}
        <p className="text-center text-xs text-gray-400 mt-10">
          All memberships start by joining the community.{' '}
          <a
            href={facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-rose-500 font-semibold hover:text-rose-600 transition-colors"
          >
            Join free on Facebook →
          </a>
        </p>
      </div>
    </section>
  );
}
